import React, { useState } from "react";
import { Loader2, MousePointerClick, Trash2, X } from "lucide-react";
import type { HappeningNowListItem } from "src/types/happeningNow";
import { ImageDropzone } from "./ImageDropzone";
import { Switch } from "src/components/ui/Switch";
import { DateTimePicker } from "src/components/ui/DateTimePicker";
import { Input, Textarea } from "src/components/ui/Input";
import { SlabButton } from "src/components/ui/SlabButton";
import { HappeningNowPlatformChips } from "./HappeningNowPlatformChips";
import { Constants } from "src/constant";

export interface EditHappeningNowFormState {
  id: string;
  title: string;
  description: string;
  targetLink: string;
  /** Optional scheduled start; empty means "visible immediately". */
  startsAtLocal: string;
  expiresAtLocal: string;
  active: boolean;
  platformAndroid: boolean;
  platformIos: boolean;
  currentImageUrl: string;
}

interface EditHappeningNowDialogProps {
  item: HappeningNowListItem;
  state: EditHappeningNowFormState;
  setState: React.Dispatch<
    React.SetStateAction<EditHappeningNowFormState | null>
  >;
  saving: boolean;
  deleting: boolean;
  error: string | null;
  labelClass: string;
  /** Read-only stats from the matching live card (if any). */
  liveClickCount?: number;
  liveLastClickedAt?: Date | null;
  onClose: () => void;
  onSubmit: (file: File | null) => Promise<void>;
  onDelete: () => Promise<void>;
}

export const EditHappeningNowDialog: React.FC<EditHappeningNowDialogProps> = ({
  state,
  setState,
  saving,
  deleting,
  error,
  labelClass,
  liveClickCount,
  liveLastClickedAt,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  const update = (patch: Partial<EditHappeningNowFormState>) =>
    setState((prev) => (prev ? { ...prev, ...patch } : prev));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(imageFile);
  };

  // Bound the start picker: before this card's expiry and at most ~1 month out
  // (mirrors parseStartsAt — same rule for draft and live edits).
  const maxStartAheadMs =
    Date.now() +
    Constants.HAPPENING_NOW_MAX_START_AHEAD_DAYS * 24 * 60 * 60 * 1000;
  const expiryMs = state.expiresAtLocal
    ? new Date(state.expiresAtLocal).getTime()
    : NaN;
  const startMax = new Date(
    Number.isNaN(expiryMs)
      ? maxStartAheadMs
      : Math.min(expiryMs, maxStartAheadMs),
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-happening-now-title"
      className="fixed inset-0 z-[300] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
    >
      <div
        className="relative max-h-full w-full overflow-y-auto rounded-t-[2rem] border-4 border-[#E5E5E5] bg-white p-5 shadow-[0_8px_0_#D0D0D0] sm:max-w-2xl sm:rounded-[2rem] sm:p-8"
        style={{ fontFamily: "'Nunito', system-ui, sans-serif" }}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2
              id="edit-happening-now-title"
              className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl"
            >
              Edit card
            </h2>
            <p className="text-sm font-bold text-[#777777]">
              Tweak the image, copy, link, or expiry.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-xl p-2 text-[#777777] hover:bg-[#F7F7F7] hover:text-[#3C3C3C]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <p className="mb-4 rounded-xl bg-[#FEE2E2] px-4 py-3 text-center text-sm font-extrabold text-[#B91C1C]">
            {error}
          </p>
        )}
        {imageError && (
          <p className="mb-4 rounded-xl bg-[#FEE2E2] px-4 py-3 text-center text-sm font-extrabold text-[#B91C1C]">
            {imageError}
          </p>
        )}

        {(typeof liveClickCount === "number" && liveClickCount > 0) ||
        liveLastClickedAt ? (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border-2 border-[#E5E5E5] bg-[#F7F7FE] p-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#EEF2FF] text-[#4338CA]">
              <MousePointerClick className="h-4 w-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-extrabold uppercase tracking-wide text-[#777777]">
                Live performance
              </p>
              <p className="text-sm font-extrabold text-[#3C3C3C]">
                {(liveClickCount ?? 0).toLocaleString()}{" "}
                {liveClickCount === 1 ? "tap" : "taps"} recorded
                {liveLastClickedAt
                  ? ` • last ${liveLastClickedAt.toLocaleString()}`
                  : ""}
              </p>
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <ImageDropzone
            initialImageUrl={state.currentImageUrl}
            file={imageFile}
            onFileChange={setImageFile}
            onError={setImageError}
          />

          <label className={labelClass}>
            Title
            <Input
              accent="teal"
              type="text"
              required
              value={state.title}
              onChange={(e) => update({ title: e.target.value })}
              maxLength={120}
            />
          </label>

          <label className={labelClass}>
            Description
            <Textarea
              accent="teal"
              value={state.description}
              onChange={(e) => update({ description: e.target.value })}
              maxLength={500}
              rows={3}
            />
          </label>

          <label className={labelClass}>
            Target link
            <Input
              accent="teal"
              type="url"
              required
              value={state.targetLink}
              onChange={(e) => update({ targetLink: e.target.value })}
            />
          </label>

          <HappeningNowPlatformChips
            groupLabel="Platforms"
            androidSelected={state.platformAndroid}
            iosSelected={state.platformIos}
            disabled={saving || deleting}
            onToggleAndroid={() => {
              const next = !state.platformAndroid;
              if (!next && !state.platformIos) return;
              update({ platformAndroid: next });
            }}
            onToggleIos={() => {
              const next = !state.platformIos;
              if (!next && !state.platformAndroid) return;
              update({ platformIos: next });
            }}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <label className={labelClass}>
              Starts showing at
              <DateTimePicker
                accent="teal"
                value={state.startsAtLocal}
                onChanged={({ value }) => update({ startsAtLocal: value })}
                end={startMax}
                aria-label="Card scheduled start date and time"
              />
              <span className="text-[11px] font-bold normal-case tracking-normal text-[#9CA3AF]">
                Leave empty to show immediately.
              </span>
            </label>
            <label className={labelClass}>
              Stops showing at
              <DateTimePicker
                accent="teal"
                value={state.expiresAtLocal}
                onChanged={({ value }) => update({ expiresAtLocal: value })}
                start={state.startsAtLocal || undefined}
                required
                aria-label="Card expiry date and time"
              />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className={`${labelClass} sm:items-start`}>
              Status
              <span className="flex min-h-[48px] items-center justify-between gap-3 rounded-2xl border-2 border-[#E5E5E5] bg-white px-4">
                <span className="text-sm font-extrabold text-[#3C3C3C]">
                  {state.active ? "Active — visible" : "Paused — hidden"}
                </span>
                <Switch
                  checked={state.active}
                  onChange={(next) => update({ active: next })}
                  aria-label="Toggle active state"
                />
              </span>
            </label>
          </div>

          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting || saving}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border-2 border-[#EF4444] bg-[#FEF2F2] px-4 py-2 text-sm font-extrabold text-[#B91C1C] shadow-[0_3px_0_#FCA5A5] active:translate-y-0.5 disabled:opacity-50"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete card
            </button>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving || deleting}
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border-2 border-[#E5E5E5] bg-white px-5 text-sm font-extrabold text-[#3C3C3C] shadow-[0_3px_0_#D0D0D0]"
              >
                Cancel
              </button>
              <SlabButton
                variant="primary"
                type="submit"
                disabled={saving || deleting}
                backgroundColor="#14B8A6"
                lipColor="#0F766E"
                shadowLiftPx={5}
                shadowLiftActivePx={2}
                className="min-h-[48px] rounded-2xl px-6 disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </SlabButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
